import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { IoIosArrowBack, IoMdInformationCircle } from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { Input, Select, Textarea, TimeInput } from '~/components';
import { useSequency } from '~/hooks/sequency';
import { useStudent } from '~/hooks/student';
import { Prescription } from '~/models/Sequencies';
import api from '~/services/api';
import { formatHMS, getSeconds } from '~/utils';
import { formatProfileCode } from '~/utils/formatProfileCode';
import getValidationErrors from '~/utils/getValidationErrors';
import { secondsToStringFormat } from '~/utils/secondsToStringFormat';

import { IProgram } from '../../../interface';
import { DivisionData, DivisionDataResults } from '../PrescriptionScreen/types';
import Modal from './Modal';
import {
  IControlPanel,
  IControlPanelForm,
  IDivisionData,
  IRouteParams,
  ISelectProps,
} from './types';

import * as S from './styles';

export function ControlPanel() {
  const { billingId } = useParams<IRouteParams>();
  const { goBack, location } = useHistory();
  const { sequencies } = useSequency();
  const { studentData } = useStudent();

  const { division, programId } = useState(
    location.state as {
      division: DivisionDataResults;
      programId: number;
    },
  )[0];

  const formRef = useRef<FormHandles>(null);

  const [showStudentInfoModal, setShowStudentInfoModal] = useState(false);
  const [panelOfControlId, setPanelOfControlId] = useState<number>();
  const [program, setProgram] = useState({} as IProgram);

  const [isLoading, setIsLoading] = useState(false);
  const [initialData, setInitialData] = useState<IControlPanelForm>(
    {} as IControlPanelForm,
  );
  const [defaultInitialCardio, setDefaultInitialCardio] =
    useState<ISelectProps>({} as ISelectProps);
  const [defaultFinalCardio, setDefaultFinalCardio] = useState<ISelectProps>(
    {} as ISelectProps,
  );
  const [sequencyPrescriptions, setSequencyPrescriptions] = useState<
    Prescription[]
  >([]);
  const [toggle, setToggle] = useState<boolean>(false);
  const [_, setToggleTotalTime] = useState(false);
  const [studentAvailableTime, setStudentAvailableTime] = useState<string>('');

  const [intervalBetweenSequency, setIntervalBetweenSequency] =
    useState<number>(0);
  const [trainingStart, setTrainingStart] = useState<number>(0);
  const [trainingEnd, setTrainingEnd] = useState<number>(0);
  const [trainingTimeOfExecution, setTrainingTimeOfExecution] =
    useState<number>(0);
  const [
    trainingTimeOfExecutionDefaultValue,
    setTrainingTimeofExecutionDefualtValue,
  ] = useState<string>(initialData.time_of_execution);

  const hasTwoOrMoreSequencies = useMemo(
    () => sequencies.length > 1,
    [sequencies],
  );

  const handleGetDivisions = useCallback(async () => {
    const response = await api.get<DivisionData>(
      `training-programs/${programId}/divisions`,
    );

    setProgram(response.data.header);
  }, [programId]);

  useEffect(() => {
    handleGetDivisions();
  }, [handleGetDivisions]);

  // load divisionId
  useEffect(() => {
    async function fetchData() {
      const { data } = await api.get<IDivisionData>(
        `training-programs/${program.id}/divisions`,
      );

      const pocId = data?.results.map((result) => result.controlPanel.id)[0];
      pocId && setPanelOfControlId(pocId);

      setStudentAvailableTime(
        `
          00:${
            data.header.datasheet.available_time.replace(`"`, '').split('-')[1]
          }:00
        `,
      );
    }

    fetchData();

    setToggle((prev) => !prev);
  }, [program]);

  useEffect(() => {
    sequencies.map((sequency) =>
      sequency.prescriptions.map((core) =>
        setSequencyPrescriptions((prev) => [...prev, core]),
      ),
    );

    setToggle((prev) => !prev);
  }, [sequencies]);

  // calculateTotalCardioTime
  useEffect(() => {
    const calc = trainingStart + trainingEnd;

    setTrainingTimeofExecutionDefualtValue(formatHMS(calc));
  }, [toggle, sequencyPrescriptions, trainingStart, trainingEnd]);

  // set data from prescriptions
  useEffect(() => {
    const numberOfSeries =
      sequencyPrescriptions.map(
        (prescriptions) => prescriptions.number_of_series,
      ).length > 0
        ? sequencyPrescriptions
            .map((prescriptions) => prescriptions.number_of_series)
            .reduce((accumulator, value) => (accumulator ?? 0) + (value ?? 0))
        : 0;

    formRef.current?.setFieldValue('number_of_series', numberOfSeries);

    const numberOfRepetitions =
      sequencyPrescriptions.map((prescriptions) => prescriptions.volume_total)
        .length > 0
        ? sequencyPrescriptions
            .map((prescriptions) => prescriptions.volume_total)
            .reduce((accumulator, value) => (accumulator ?? 0) + (value ?? 0))
        : 0;

    formRef.current?.setFieldValue(
      'number_of_repetitions',
      numberOfRepetitions,
    );

    const workOutTime = sequencyPrescriptions.map(
      (prescriptions) => prescriptions.total_action,
    );

    const workOutTimeNumber =
      workOutTime?.length > 0 &&
      workOutTime
        ?.map((value) => getSeconds(value))
        ?.reduce((accumulator, value) => (accumulator ?? 0) + (value ?? 0));

    workOutTime?.length > 0 &&
      formRef.current?.setFieldValue(
        'workout_time',
        workOutTimeNumber && formatHMS(workOutTimeNumber),
      );

    const recoveryTotal = sequencyPrescriptions.map(
      (prescriptions) => prescriptions.recovery_total,
    );

    const recoveryTotalNumber =
      recoveryTotal?.length > 0 &&
      recoveryTotal
        ?.map((value) => getSeconds(value))
        ?.reduce((accumulator, value) => (accumulator ?? 0) + (value ?? 0));

    recoveryTotal?.length > 0 &&
      formRef.current?.setFieldValue(
        'recovery_total',
        recoveryTotalNumber && formatHMS(recoveryTotalNumber),
      );

    workOutTimeNumber &&
      recoveryTotalNumber &&
      formRef.current?.setFieldValue(
        'total_time',
        formatHMS(
          workOutTimeNumber +
            recoveryTotalNumber +
            getSeconds(formRef.current?.getFieldValue('time_of_execution')),
        ),
      );

    workOutTimeNumber &&
    recoveryTotalNumber &&
    workOutTimeNumber +
      recoveryTotalNumber +
      getSeconds(formRef.current?.getFieldValue('time_of_execution')) >
      getSeconds(studentAvailableTime)
      ? setToggleTotalTime(true)
      : setToggleTotalTime(false);
  }, [toggle, sequencyPrescriptions, studentAvailableTime]);

  const cardioVariations = useMemo(
    () => [
      {
        label: 'BIKE DE SPINNING',
        value: 'BIKE DE SPINNING',
      },
      {
        label: 'BIKE ERGOMÉTRICA',
        value: 'BIKE ERGOMÉTRICA',
      },
      {
        label: 'CORRIDA ESTACIONÁRIA',
        value: 'CORRIDA ESTACIONÁRIA',
      },
      {
        label: 'CORRIDA MOUNTAIN CLIMB',
        value: 'CORRIDA MOUNTAIN CLIMB',
      },
      {
        label: 'ESTEIRA',
        value: 'ESTEIRA',
      },
      {
        label: 'JUMP',
        value: 'JUMP',
      },
      {
        label: 'POLICHINELO',
        value: 'POLICHINELO',
      },
      {
        label: 'SIMULADOR DE ESCADA',
        value: 'SIMULADOR DE ESCADA',
      },
      {
        label: 'TRANSPORT',
        value: 'TRANSPORT',
      },
    ],
    [],
  );

  // fetch controlPanel data
  const fetchControlPanel = useCallback(async () => {
    const { data } = await api.get<IControlPanel>(
      `divisions/${division.id}/control-panel`,
    );

    const initialFormData = {
      ...data,
      training_start: formatHMS(data.training_start),
      training_end: formatHMS(data.training_end),
    };

    if (data.interval_between_sequence > 0) {
      initialFormData.interval_between_sequence = formatHMS(
        data.interval_between_sequence,
      ) as any;
    }

    setInitialData(initialFormData as any);

    const initialCardio = cardioVariations.filter(
      (variation) => variation.label === data.cardio_aerobic,
    )[0];
    setDefaultInitialCardio(initialCardio);

    const finalCardio = cardioVariations.filter(
      (variation) => variation.label === data.cardio_aerobic_final,
    )[0];
    setDefaultFinalCardio(finalCardio);

    setToggle((prev) => !prev);
  }, [division, cardioVariations]);

  useEffect(() => {
    fetchControlPanel();
  }, [fetchControlPanel]);

  function StudentInfoModal() {
    const genderVariations: { [key: string]: string } = {
      male: 'MASCULINO',
      female: 'FEMININO',
      others: 'OUTRO',
      other: 'OUTRO',
      '': 'Não informado',
      null: 'Não informado',
    };

    return (
      <Modal
        isOpen={showStudentInfoModal}
        setOpen={setShowStudentInfoModal}
        maxWidthStyled={600}
      >
        <S.ModalContainer>
          <h1>Informações do Treino</h1>

          <S.ContentContainer>
            <S.RowContent>
              <strong>Fatura: </strong>
              <p>{billingId}</p>
            </S.RowContent>

            <S.RowContent>
              <strong>Programa: </strong>
              {programId}
            </S.RowContent>

            <S.RowContent>
              <strong>Nome do aluno: </strong>
              {studentData.name}
            </S.RowContent>

            <S.RowContent>
              <strong>Sexo: </strong>
              {genderVariations[studentData.student.genre]}
            </S.RowContent>

            <S.RowContent>
              <strong>Nível do aluno: </strong>
              {program?.datasheet?.student_level}
            </S.RowContent>

            <S.RowContent>
              <strong>Avaliação: </strong>
              {program?.datasheet?.classification}
            </S.RowContent>

            <S.RowContent>
              <strong>Objetivo: </strong>
              {program?.datasheet?.objective}
            </S.RowContent>

            <S.RowContent>
              <strong>Frequência: </strong>
              {program?.datasheet?.frequency}
            </S.RowContent>

            <S.RowContent>
              <strong>Tempo disponível: </strong>
              {program?.datasheet?.available_time}
            </S.RowContent>

            <S.RowContent>
              <strong>Divisão de treino: </strong>
              {program?.training_division}
            </S.RowContent>
          </S.ContentContainer>

          <div className="profile-code">
            <h2>Profile Code:</h2>
            <h3>{formatProfileCode(program?.datasheet?.profile_code)}</h3>
          </div>

          <div className="sheet-model">
            <h2>Modelo de ficha:</h2>
            <h3>{program?.data_model}</h3>
          </div>
        </S.ModalContainer>
      </Modal>
    );
  }

  const selectStyle = {
    placeholder: () => ({
      padding: '0 1rem',
      color: '#00000080',
    }),
    singleValue: () => ({
      padding: '0 1rem',
    }),
  };

  const handleSubmit = useCallback(
    async (data: IControlPanelForm) => {
      setIsLoading(true);

      const formData = {
        ...data,
        interval_between_sequence: intervalBetweenSequency,
        training_end: trainingEnd,
        training_start: trainingStart,
        status: true,
        training_division_id: division.id,
        time_of_execution:
          trainingTimeOfExecution === 0
            ? '00:00:00'
            : secondsToStringFormat(trainingTimeOfExecution),
        recovery_total:
          data.recovery_total === '0' ? '00:00:00' : data.recovery_total,
      };

      const schema = Yup.object().shape({
        cardio_aerobic: Yup.string(),
        cardio_aerobic_final: Yup.string(),
        interval_between_sequence: Yup.number().required(
          'O campo é obrigatório',
        ),
        number_of_series: Yup.number().required('O campo é obrigatório '),
        time_of_execution: Yup.string().required('O campo é obrigatório'),
        training_end: Yup.number(),
        training_start: Yup.number(),
        observations: Yup.string(),
      });

      try {
        await schema.validate(formData, { abortEarly: false });

        await api.put(`/controlpanels/${panelOfControlId}`, formData);

        toast.success(`Treino finalizada com sucesso!`);

        goBack();
      } catch (err: any) {
        console.log(err);

        const errors = getValidationErrors(err as any);

        formRef.current?.setErrors(errors);

        toast.error('Preencha os campos obrigatórios e tente novamente!');
      }

      setIsLoading(false);
    },
    [
      division,
      goBack,
      intervalBetweenSequency,
      panelOfControlId,
      trainingEnd,
      trainingStart,
      trainingTimeOfExecution,
    ],
  );

  return (
    <>
      <S.PageHeader>
        <S.HeaderWrapper>
          <S.FirstScreenHeaderContainer>
            <S.PageHeaderBackButton onClick={() => goBack()}>
              <IoIosArrowBack />

              <S.PageHeaderBackButtonLabel>
                protocolo de treino
              </S.PageHeaderBackButtonLabel>
            </S.PageHeaderBackButton>

            <S.FinishTrainingButton
              disabled={isLoading}
              onClick={() => formRef.current?.submitForm()}
            >
              Finalizar Treino
            </S.FinishTrainingButton>
          </S.FirstScreenHeaderContainer>

          <S.Info>
            <S.InfoContent>
              <strong>Nome: </strong>
              <p>{studentData.name}</p>
            </S.InfoContent>

            <StudentInfoModal />

            <S.InfoButton
              type="button"
              onClick={() => setShowStudentInfoModal(true)}
            >
              <IoMdInformationCircle color="#ffdf22" size={24} />
            </S.InfoButton>
          </S.Info>

          <S.Info>
            <S.InfoContent>
              <strong>DIVISÃO: </strong>
              <p>{division?.division}</p>
            </S.InfoContent>

            <S.InfoContent>
              <strong>Segmento: </strong>
              <p>{division?.muscle_group}</p>
            </S.InfoContent>
          </S.Info>
        </S.HeaderWrapper>
      </S.PageHeader>

      <S.Container>
        <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
          <div className="form-inputs">
            <S.InputTimeFormSection>
              <label htmlFor="interval_between_sequence">
                Intervalo entre troca de sequência
              </label>
              <TimeInput
                disabled={!hasTwoOrMoreSequencies}
                defaultValue={initialData?.interval_between_sequence || ''}
                setValue={setIntervalBetweenSequency}
              />
            </S.InputTimeFormSection>

            <S.FormSection>
              <label htmlFor="number_of_series">
                Volume total - n° de séries
              </label>
              <Input
                name="number_of_series"
                id="number_of_series"
                disabled
                placeholder="9"
              />
            </S.FormSection>

            <S.FormSelect>
              <label htmlFor="cardio_aerobic">
                Cardio/aeróbico: Início do treino
              </label>
              <Select
                key={`rerender_key_${
                  defaultInitialCardio && defaultInitialCardio.label
                }`}
                name="cardio_aerobic"
                id="cardio_aerobic"
                options={cardioVariations}
                defaultValue={defaultInitialCardio ?? cardioVariations[0]}
                styles={selectStyle}
                isSearchable={false}
              />
            </S.FormSelect>

            <S.InputTimeFormSection>
              <label htmlFor="training_start">Início do treino</label>
              <TimeInput
                defaultValue={initialData.training_start}
                setValue={setTrainingStart}
              />
            </S.InputTimeFormSection>

            <S.FormSelect>
              <label htmlFor="cardio_aerobic_final">
                Cardio/aeróbico: Final do treino
              </label>
              <Select
                key={`rerender_key_${
                  defaultFinalCardio && defaultFinalCardio.label
                }`}
                name="cardio_aerobic_final"
                id="cardio_aerobic_final"
                options={cardioVariations}
                defaultValue={defaultFinalCardio ?? cardioVariations[0]}
                styles={selectStyle}
                isSearchable={false}
              />
            </S.FormSelect>

            <S.InputTimeFormSection>
              <label htmlFor="training_end">Final do treino</label>
              <TimeInput
                defaultValue={initialData.training_end}
                setValue={setTrainingEnd}
              />
            </S.InputTimeFormSection>

            <S.InputTimeFormSection>
              <label htmlFor="time_of_execution">
                Tempo total Cardio/aeróbico
              </label>
              <TimeInput
                disabled
                defaultValue={trainingTimeOfExecutionDefaultValue}
                setValue={setTrainingTimeOfExecution}
              />
            </S.InputTimeFormSection>
          </div>

          <div className="full-row-input">
            <S.FormSection>
              <label htmlFor="observations">Observações</label>
              <Textarea
                placeholder="Separe as informações com ';'"
                name="observations"
                id="observations"
                containerStyle={{
                  padding: '12rem 2rem',
                }}
              />
            </S.FormSection>
          </div>
        </Form>
      </S.Container>
    </>
  );
}
